import React, { useEffect, useRef, useState } from "react";
import { Video, Upload, Square, RotateCcw, Loader2, CheckCircle2, X } from "lucide-react";
import api from "../api";
import { toast } from "../hooks/use-toast";

const MAX_SECONDS = 30;
const MAX_BYTES = 30 * 1024 * 1024;

const pickMime = () => {
  const opts = ["video/mp4", "video/webm;codecs=vp9,opus", "video/webm;codecs=vp8,opus", "video/webm"];
  return opts.find((m) => window.MediaRecorder && MediaRecorder.isTypeSupported(m)) || "";
};

const videoDuration = (file) => new Promise((resolve) => {
  const el = document.createElement("video");
  el.preload = "metadata";
  el.onloadedmetadata = () => { URL.revokeObjectURL(el.src); resolve(el.duration); };
  el.onerror = () => resolve(0);
  el.src = URL.createObjectURL(file);
});

export const VideoRecorder = ({ slug, uploadPath, value, onChange, label }) => {
  const [mode, setMode] = useState("idle"); // idle | recording | uploading | done
  const [seconds, setSeconds] = useState(0);
  const [preview, setPreview] = useState("");
  const liveRef = useRef(null);
  const recRef = useRef(null);
  const streamRef = useRef(null);
  const fileRef = useRef(null);

  const stopStream = () => { streamRef.current?.getTracks().forEach((t) => t.stop()); streamRef.current = null; };
  useEffect(() => () => stopStream(), []);
  useEffect(() => {
    if (mode !== "recording") return undefined;
    const t = setInterval(() => setSeconds((s) => {
      if (s + 1 >= MAX_SECONDS) recRef.current?.state === "recording" && recRef.current.stop();
      return s + 1;
    }), 1000);
    return () => clearInterval(t);
  }, [mode]);

  const upload = async (blob, name) => {
    if (blob.size > MAX_BYTES) { toast({ title: "Video too large", description: "Max 30MB.", variant: "destructive" }); return; }
    setMode("uploading");
    setPreview(URL.createObjectURL(blob));
    try {
      const fd = new FormData();
      fd.append("file", blob, name);
      const { data } = await api.post(uploadPath || `/store/${slug}/video`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      onChange(data.url);
      setMode("done");
    } catch (err) {
      toast({ title: "Upload failed", description: err?.response?.data?.detail || "Try again", variant: "destructive" });
      reset();
    }
  };

  const start = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: "user" }, audio: true });
      streamRef.current = stream;
      const mime = pickMime();
      const rec = new MediaRecorder(stream, mime ? { mimeType: mime } : undefined);
      const chunks = [];
      rec.ondataavailable = (e) => e.data.size && chunks.push(e.data);
      rec.onstop = () => {
        stopStream();
        const type = (rec.mimeType || "video/webm").split(";")[0];
        upload(new Blob(chunks, { type }), `message.${type.includes("mp4") ? "mp4" : "webm"}`);
      };
      recRef.current = rec;
      setSeconds(0);
      setMode("recording");
      rec.start(250);
      setTimeout(() => { if (liveRef.current) liveRef.current.srcObject = stream; }, 0);
    } catch {
      toast({ title: "Camera unavailable", description: "Allow camera & microphone access, or upload a clip instead.", variant: "destructive" });
    }
  };

  const pickFile = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    const dur = await videoDuration(f);
    if (dur > MAX_SECONDS + 1) { toast({ title: "Video too long", description: `Please keep it under ${MAX_SECONDS} seconds.`, variant: "destructive" }); return; }
    upload(f, f.name);
  };

  const reset = () => { stopStream(); setPreview(""); setMode("idle"); setSeconds(0); onChange(""); };

  return (
    <div className="rounded-xl border border-dashed border-gray-300 p-4" data-testid="video-recorder">
      <p className="text-sm font-medium text-[#16243b] flex items-center gap-1.5"><Video size={16} /> {label || `Video message (optional, up to ${MAX_SECONDS}s)`}</p>
      {mode === "idle" && (
        <div className="flex gap-2 mt-3">
          <button type="button" onClick={start} data-testid="video-record-btn" className="gp-btn-teal flex-1 rounded-xl py-2.5 text-sm font-medium inline-flex items-center justify-center gap-1.5"><Video size={15} /> Record</button>
          <button type="button" onClick={() => fileRef.current?.click()} data-testid="video-upload-btn" className="gp-btn-outline flex-1 rounded-xl py-2.5 text-sm font-medium inline-flex items-center justify-center gap-1.5"><Upload size={15} /> Upload clip</button>
          <input ref={fileRef} type="file" accept="video/mp4,video/webm,video/quicktime,.mov,.mp4,.webm" className="hidden" onChange={pickFile} data-testid="video-file-input" />
        </div>
      )}
      {mode === "recording" && (
        <div className="mt-3">
          <video ref={liveRef} autoPlay muted playsInline className="w-full rounded-lg bg-black aspect-video object-cover" />
          <div className="flex items-center justify-between mt-2">
            <span className="text-sm font-mono text-red-500 flex items-center gap-1.5" data-testid="video-timer"><span className="h-2 w-2 rounded-full bg-red-500 animate-pulse" /> 0:{String(seconds).padStart(2, "0")} / 0:{MAX_SECONDS}</span>
            <button type="button" onClick={() => recRef.current?.stop()} data-testid="video-stop-btn" className="rounded-xl bg-[#16243b] text-white px-4 py-2 text-sm inline-flex items-center gap-1.5"><Square size={13} /> Stop</button>
          </div>
        </div>
      )}
      {(mode === "uploading" || mode === "done") && (
        <div className="mt-3">
          {preview && <video src={preview} controls playsInline className="w-full rounded-lg bg-black aspect-video" data-testid="video-preview" />}
          <div className="flex items-center justify-between mt-2 text-sm">
            {mode === "uploading"
              ? <span className="text-[#5a6b80] flex items-center gap-1.5"><Loader2 size={14} className="animate-spin" /> Uploading…</span>
              : <span className="text-green-600 font-medium flex items-center gap-1.5" data-testid="video-attached"><CheckCircle2 size={15} /> Video attached</span>}
            <span className="flex gap-3">
              <button type="button" onClick={reset} data-testid="video-rerecord-btn" className="text-[#5a6b80] hover:text-[#16243b] inline-flex items-center gap-1"><RotateCcw size={13} /> Redo</button>
              <button type="button" onClick={reset} data-testid="video-remove-btn" className="text-red-500 inline-flex items-center gap-1"><X size={13} /> Remove</button>
            </span>
          </div>
        </div>
      )}
      {value && mode === "idle" && <p className="text-xs text-green-600 mt-2">Video attached</p>}
    </div>
  );
};
